<template>
  <div class="flex flex-col items-start gap-4 self-stretch">
    <!-- Show Image Toggle -->
    <Switch
      :label="t('common.style.imageLayout.showImage')"
      :value="showImage"
      @update:value="handleShowImageToggle"
    />

    <!-- Image Upload Section (only when showImage is true) -->
    <div v-if="showImage" class="flex flex-col items-start gap-2 self-stretch">
      <ImageUpload
        :header="t('common.style.imageLayout.image')"
        field="store.deviceMode === 'mobile' ? 'mobileHeaderImageSrc' : 'headerImageSrc'"
        :is-survey="isSurvey"
        :selected-slide-index="selectedSlideIndex"
        :is-editing-survey-default="isEditingSurveyDefault"
      />
    </div>

    <!-- Image Layout Section (only when showImage is true and image exists) -->
    <div v-if="shouldShowLayoutOptions" class="flex flex-col items-start gap-1 self-stretch">
      <div class="mb-2 flex items-center justify-between">
        <UITextXsMedium class="mr-2">{{
          t("common.style.imageLayout.imageLayout")
        }}</UITextXsMedium>
        <UIDropdown
          id="image-layout-device-toggle"
          :options="deviceOptions"
          @select="switchDeviceMode"
        >
          <template #default>
            <div class="flex items-center gap-0.5">
              <Phone01Icon v-if="store.deviceMode === 'mobile'" class="h-4 w-4 cursor-pointer" />
              <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
              <ChevronDownIcon class="h-3 w-3" />
            </div>
          </template>
        </UIDropdown>
      </div>

      <!-- Layout Options Grid -->
      <div class="grid grid-cols-3 gap-3 self-stretch">
        <!-- Desktop Layout Options -->
        <template v-if="store.deviceMode !== 'mobile'">
          <div
            v-for="option in desktopLayoutOptions"
            :key="option.value"
            class="layout-container"
            @click="handleLayoutChange(option.value)"
          >
            <div
              class="layout-card"
              :class="{ 'layout-card-active': currentImageLayout === option.value }"
            >
              <div class="layout-card-icon">
                <component :is="option.icon" />
              </div>
            </div>
            <UITextXsRegular>{{ option.label }}</UITextXsRegular>
          </div>
        </template>

        <!-- Mobile Layout Options -->
        <template v-else>
          <div
            v-for="option in mobileLayoutOptions"
            :key="option.value"
            class="layout-container"
            @click="handleLayoutChange(option.value)"
          >
            <div
              class="layout-card"
              :class="{ 'layout-card-active': currentImageLayout === option.value }"
            >
              <div class="layout-card-icon">
                <component :is="option.icon" />
              </div>
            </div>
            <UITextXsRegular>{{ option.label }}</UITextXsRegular>
          </div>
        </template>
      </div>
    </div>

    <!-- Focus Point Settings (only for leftFixed/rightFixed layouts) -->
    <FocusPointSettings
      v-if="shouldShowLayoutOptions"
      :is-survey="isSurvey"
      :selected-slide-index="selectedSlideIndex"
      :is-editing-survey-default="isEditingSurveyDefault"
    />

    <!-- Image Layout Controls (only for leftCenter/rightCenter layouts) -->
    <ImageLayoutControls
      v-if="shouldShowLayoutOptions"
      :is-survey="isSurvey"
      :is-editing-survey-default="isEditingSurveyDefault"
      :selected-slide-index="selectedSlideIndex"
      :desktop-image-layout="currentDesktopLayout"
      :mobile-image-layout="currentMobileLayout"
      :show-image="showImage"
      :image-field-spacing="imageFieldSpacing"
      :image-corner-radius="imageCornerRadius"
      @update:image-field-spacing="handleImageFieldSpacing"
      @update:image-corner-radius="handleImageCornerRadius"
    />
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import {
  deviceOptions,
  getDesktopLayoutOptions,
  getMobileLayoutOptions,
  IMAGE_LAYOUT,
  SURVEY_MODE,
} from "@/util/constants";
import { ChevronDownIcon, Monitor04Icon, Phone01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIDropdown, UITextXsMedium, UITextXsRegular } from "@gohighlevel/ghl-ui";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import FocusPointSettings from "../Elements/FocusPointSettings.vue";
import ImageLayoutControls from "../Elements/ImageLayoutControls.vue";
import ImageUpload from "../Elements/ImageUpload.vue";
import Switch from "../Elements/Switch.vue";

const { t } = useI18n();
const store = useAppStore();

// Props
const props = defineProps<{
  showImage?: boolean;
  desktopImageLayout?: string;
  mobileImageLayout?: string;
  headerImageSrc?: string;
  mobileHeaderImageSrc?: string;
  imageFieldSpacing?: number;
  imageCornerRadius?: number;
  isSurvey?: boolean;
  selectedSlideIndex?: number;
  isEditingSurveyDefault?: boolean;
}>();

// Emits
const emit = defineEmits<{
  "update:showImage": [value: boolean];
  "update:desktopImageLayout": [value: string];
  "update:mobileImageLayout": [value: string];
  "update:headerImageSrc": [value: string];
  "update:mobileHeaderImageSrc": [value: string];
  "update:imageFieldSpacing": [value: number];
  "update:imageCornerRadius": [value: number];
}>();

// Desktop layout options
const desktopLayoutOptions = computed(() => getDesktopLayoutOptions(t));

// Mobile layout options
const mobileLayoutOptions = computed(() => getMobileLayoutOptions(t));

// Computed properties
const showImage = computed(() => {
  if (props.isSurvey) {
    // Survey mode - use props
    return props.showImage;
  }
  // Form mode - use store
  return store.builderConfig.showImage || false;
});

const currentImageLayout = computed(() => {
  if (props.isSurvey) {
    // Survey mode - use props
    if (store.deviceMode === "mobile") {
      return props.mobileImageLayout || IMAGE_LAYOUT.HEADER;
    }
    return props.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
  }
  // Form mode - use store
  if (store.deviceMode === "mobile") {
    return store.builderConfig.mobileImageLayout || IMAGE_LAYOUT.HEADER;
  }
  return store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
});

const currentDesktopLayout = computed(() => {
  if (props.isSurvey) {
    return props.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
  }
  return store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
});

const currentMobileLayout = computed(() => {
  if (props.isSurvey) {
    return props.mobileImageLayout || IMAGE_LAYOUT.HEADER;
  }
  return store.builderConfig.mobileImageLayout || IMAGE_LAYOUT.HEADER;
});

// Check if header image exists
const hasImageForCurrentDevice = computed(() => {
  if (props.isSurvey) {
    const slideId = store.slides[props.selectedSlideIndex || 0]?.id;
    const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];
    const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;
    const settings = props.isEditingSurveyDefault
      ? store.surveyImageSettings.surveyDefault
      : slideMode === SURVEY_MODE.SURVEY_DEFAULT
        ? store.surveyImageSettings.surveyDefault
        : slideSettings;

    const hasDesktopImage = !!(settings?.headerImageSrc && settings.headerImageSrc.trim());
    const hasMobileImage = !!(settings?.mobileHeaderImageSrc && settings.mobileHeaderImageSrc.trim());
    const mobileImageDeleted = Boolean((settings as any)?.mobileHeaderImageSrcDeleted);

    if (store.deviceMode === "mobile") {
      return hasMobileImage || (!mobileImageDeleted && hasDesktopImage);
    }

    return hasDesktopImage;
  }

  const hasDesktopImage = !!(store.formAction.headerImageSrc && store.formAction.headerImageSrc.trim());
  const hasMobileImage = !!(
    store.formAction.mobileHeaderImageSrc && store.formAction.mobileHeaderImageSrc.trim()
  );
  const mobileImageDeleted = Boolean(store.formAction.mobileHeaderImageSrcDeleted);

  if (store.deviceMode === "mobile") {
    return hasMobileImage || (!mobileImageDeleted && hasDesktopImage);
  }

  return hasDesktopImage;
});

// Show layout options only when showImage is true and image exists
const shouldShowLayoutOptions = computed(() => {
  return showImage.value && hasImageForCurrentDevice.value;
});

// Methods
const switchDeviceMode = (option: string) => {
  store.deviceMode = option;
};

const handleShowImageToggle = (value: boolean) => {
  if (props.isSurvey) {
    // Survey mode - emit the event to parent component
    emit("update:showImage", value);
  } else {
    // Form mode - use existing logic
    store.builderConfig.showImage = value;

    // If turning off showImage, clear the images for backward compatibility
    if (!value) {
      store.formAction.headerImageSrc = "";
      store.formAction.mobileHeaderImageSrc = "";
    }
  }

  store.anyUnsavedChanges = true;
};

const handleLayoutChange = (layoutValue: string) => {
  if (props.isSurvey) {
    // Survey mode - emit the event to parent component
    if (store.deviceMode === "mobile") {
      emit("update:mobileImageLayout", layoutValue);
    } else {
      emit("update:desktopImageLayout", layoutValue);
      // Auto-adjust padding for survey in desktop mode
      autoAdjustPaddingForLayout(layoutValue);
    }
  } else if (store.deviceMode === "mobile") {
    // Form mode - use existing logic
    store.builderConfig.mobileImageLayout = layoutValue;
  } else {
    store.builderConfig.desktopImageLayout = layoutValue;
    // Auto-adjust padding for form in desktop mode
    autoAdjustPaddingForLayout(layoutValue);
  }
  store.applyLabelWidthForSidebarImageLayout(store.fieldStyle.labelAlignment);
  store.anyUnsavedChanges = true;
};

const handleImageFieldSpacing = (val: number) => {
  if (props.isSurvey) {
    emit("update:imageFieldSpacing", val);
  } else {
    store.builderConfig.imageFieldSpacing = val;
    store.anyUnsavedChanges = true;
  }
};

const handleImageCornerRadius = (val: number) => {
  if (props.isSurvey) {
    emit("update:imageCornerRadius", val);
  } else {
    store.builderConfig.imageCornerRadius = val;
    store.anyUnsavedChanges = true;
  }
};

/**
 * Auto-adjust form/survey padding based on image layout selection
 * - left-fixed/right-fixed: set left and right padding to 24px
 * - left-center/right-center: set left and right padding to imageFieldSpacing for symmetry
 */
const autoAdjustPaddingForLayout = (layoutValue: string) => {
  // Only adjust in desktop mode
  if (store.deviceMode === "mobile") {
    return;
  }

  // Ensure padding object exists
  if (!store.formStyle.padding) {
    store.formStyle.padding = { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const imageFieldSpacing = props.isSurvey
    ? store.surveyImageSettings?.surveyDefault?.imageFieldSpacing ?? 24
    : store.builderConfig.imageFieldSpacing ?? 24;

  if (layoutValue === IMAGE_LAYOUT.LEFT_FIXED || layoutValue === IMAGE_LAYOUT.RIGHT_FIXED) {
    // Set left and right padding to 24px for fixed layouts
    store.formStyle.padding.left = 24;
    store.formStyle.padding.right = 24;
  } else if (
    layoutValue === IMAGE_LAYOUT.LEFT_CENTER ||
    layoutValue === IMAGE_LAYOUT.RIGHT_CENTER
  ) {
    // Set both left and right padding to imageFieldSpacing for symmetric layout
    store.formStyle.padding.left = imageFieldSpacing;
    store.formStyle.padding.right = imageFieldSpacing;
  }
};

// Watch for showImage changes in survey mode to clear images when toggled off
watch(
  () => props.showImage,
  (newValue) => {
    if (props.isSurvey && !newValue) {
      // Clear images when showImage is turned off
      emit("update:headerImageSrc", "");
      emit("update:mobileHeaderImageSrc", "");
    }
  }
);
</script>

<style scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  justify-self: center;
}

.layout-card {
  display: flex;
  height: 100px;
  width: 108px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  background: #ffffff;
}

.layout-card-active {
  border: 2px solid #155eef;
  background: #eff4ff;
}

.layout-card-icon {
  padding: 0px 16px;
}
</style>
